/*
Andreas Arvidsson
https://www.w3.org/TR/wai-aria-practices-1.1/examples/treeview/treeview-2/treeview-2a.html
https://www.w3.org/TR/wai-aria-practices-1.1/examples/listbox/listbox-scrollable.html
*/

import type {
    ComponentChildren,
    TargetedKeyboardEvent,
    TargetedMouseEvent,
} from "preact";
import type { ForwardedRef } from "preact/compat";
import { forwardRef } from "preact/compat";
import { useEffect, useRef, useState } from "preact/hooks";
import { classNames } from "@gss/common/classNames";
import { Expand } from "@gss/components/icons";
import { useConst } from "@gss/hooks/useConst";
import type { TreeNode } from "../filters/filters.types";
import "./List.css";

let nextId = 1;

interface Props {
    nodes: TreeNode[];
    render: (node: TreeNode) => ComponentChildren;
    onExpand?: (node: TreeNode) => TreeNode | undefined;
    onClose?: (node: TreeNode) => TreeNode | undefined;
    onSelect?: (node: TreeNode, key: string) => void;
    className?: string;
    disabled?: boolean;
}

export const ListTree = forwardRef(
    (
        {
            nodes,
            render,
            onExpand,
            onClose,
            onSelect,
            className,
            disabled,
            ...rest
        }: Props,
        fRef: ForwardedRef<HTMLUListElement>,
    ) => {
        const [active, setActive] = useState(nodes[0]);
        const id = useConst(() => `list-tree-${nextId++}`);
        const [search, setSearch] = useState({ value: "", time: 0 });
        const localRef = useRef<HTMLUListElement>(null);
        const ref = fRef ?? localRef;
        const activeId = `${id}-active`;

        useEffect(() => {
            if (!nodes.includes(active)) {
                setActive(nodes[0]);
            }
        }, [nodes]);

        // Search for element with search text in name
        useEffect(() => {
            const n = nodes.find((n) =>
                n.name.toLowerCase().startsWith(search.value),
            );
            if (n != null) {
                setActive(n);
            }
        }, [search]);

        const moveActive = (diff: number | "Home" | "End") => {
            let n;
            if (diff === "Home") {
                n = nodes[0];
            } else if (diff === "End") {
                n = nodes[nodes.length - 1];
            } else {
                const i = nodes.indexOf(active) + diff;
                if (i < 0 || i >= nodes.length) {
                    return;
                }
                n = nodes[i];
            }
            if (n !== active) {
                setActive(n);
            }
        };

        const onKeyDown = (e: TargetedKeyboardEvent<HTMLUListElement>) => {
            switch (e.key) {
                case "ArrowUp":
                    moveActive(-1);
                    break;
                case "ArrowDown":
                    moveActive(1);
                    break;
                case "Home":
                case "End":
                    moveActive(e.key);
                    break;
                case "ArrowLeft":
                    if (onClose) {
                        const newActive = onClose(active);
                        if (newActive != null) {
                            setActive(newActive);
                        }
                    }
                    break;
                case "ArrowRight":
                    if (onExpand && active.children.length > 0) {
                        const newActive = onExpand(active);
                        if (newActive != null) {
                            setActive(newActive);
                        }
                    }
                    break;
                case " ":
                case "Enter":
                    if (onSelect) {
                        onSelect(active, e.key);
                    }
                    break;
                default:
                    if (e.key.length === 1) {
                        const key = e.key.toLowerCase();
                        const time = Date.now();
                        setSearch({
                            time,
                            value:
                                time - search.time > 1000
                                    ? key
                                    : search.value + key,
                        });
                    } else {
                        // Important so we don't run preventDefault() for unknown keys
                        return;
                    }
            }
            e.preventDefault();
        };

        const onMouseOver = (n: TreeNode) => {
            if (n !== active) {
                setActive(n);
            }
        };

        const onClickFocus = (e: TargetedMouseEvent<HTMLUListElement>) => {
            localRef.current?.focus();
            e.stopPropagation();
        };

        return (
            <ul
                ref={ref}
                role="tree"
                aria-activedescendant={activeId}
                className={classNames("list-tree", className)}
                tabIndex={disabled ? -1 : 0}
                onClick={disabled ? undefined : onClickFocus}
                onKeyDown={disabled ? undefined : onKeyDown}
                {...rest}
            >
                {nodes.map((n) => {
                    const isActive = n === active;
                    return (
                        <li
                            key={n.dn}
                            id={isActive ? activeId : undefined}
                            role="treeitem"
                            aria-expanded={
                                n.children.length > 0 ? "true" : undefined
                            }
                            onMouseOver={
                                disabled ? undefined : () => onMouseOver(n)
                            }
                        >
                            <div>{render(n)}</div>
                            {n.children.length > 0 && (
                                <button
                                    type="button"
                                    onClick={
                                        disabled || onExpand == null
                                            ? undefined
                                            : () => onExpand(n)
                                    }
                                    tabIndex={-1}
                                    aria-hidden
                                >
                                    <Expand />
                                </button>
                            )}
                        </li>
                    );
                })}
            </ul>
        );
    },
);

ListTree.displayName = "ListTree";
